import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BcmService } from '@modules/bcm/bcm.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { BcmExportExcelApiService } from '@bcmApiServices/bcm-export-excel.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmBaseFacade } from '@modules/bcm/@core/state-management/bcm-base.facade';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { ThemePalette } from '@angular/material/core';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import {
    filteredListExportHeaderActionBase,
    predefinedReportsExcelHeaderActionBase,
} from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import { BcmCostCenter, BcmCostCenterDto } from '@shared/models/bcm-cost-center';
import { costCentersTableData } from '@modules/bcm/@core/state-management/cost-centers/cost-centers-table-data';

@Injectable({providedIn: 'root'})
export class CostCentersFacade extends BcmBaseFacade<BcmCostCenter, BcmCostCenterDto> {

    readonly resourcePath = 'cost-centers';

    get resourceNameSingular(): string {
        return this._tolgee.translate('bcmCostCenters.singular');
    }

    get resourceNamePlural(): string {
        return this._tolgee.translate('bcmCostCenters.plural');
    }

    readonly resourceIconPlural = 'account_balance';

    readonly resourceIconSingular = 'account_balance';

    readonly deletePermission = BcmUserPermission.COST_CENTERS_DELETE;

    readonly readPermission = BcmUserPermission.COST_CENTERS_READ;

    readonly writePermission = BcmUserPermission.COST_CENTERS_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
        {
            titleKey: 'table.excelExportPositions',
            onClick: () => this._bcmExportExcelApiService.exportPositionsByCostCenters(),
            ...predefinedReportsExcelHeaderActionBase(),
        }
    ];

    readonly rowActions = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (costCenter: BcmCostCenter) => this._bcmNavigationService.navigate('cost-centers/' + costCenter.id),
        },
        {
            titleKey: 'table.rowActionDelete',
            icon: 'delete',
            iconColor: 'warn' as ThemePalette,
            onClick: (costCenter: BcmCostCenter) => super.remove(costCenter, costCenter.name).subscribe(),
        }
    ];

    buildPayloadFromForm(formValue: Record<string, unknown>): Record<string, unknown> {
        return {
            name: formValue.name,
            abbreviation: formValue.abbreviation,
            number: formValue.number,
            description: formValue.description,
            departmentId: formValue.departmentId ?? null,
            overheadSurchargePercent: formValue.overheadSurchargePercent ?? null,
        };
    }

    public saveRegisteredForm(): void {
        if (!this.hasRegisteredFormGroup) {
            throw Error('<facade>.saveRegisteredForm() can only be called if a formGroup was registered.');
        }
        if (!this.canWrite) {
            this.appNotificationService.showError(this._tolgee.translate('common.error.noSavePermission', {
                permission: this.resourceNamePlural,
            }));
            return;
        }
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this.appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        this.addOrUpdate(
            this.selected,
            this.buildPayloadFromForm(this.formGroup.getRawValue()) as Partial<BcmCostCenter>,
        ).subscribe(() => this.formGroup.markAsPristine());
    }

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                bcmNavigationService: BcmNavigationService,
                private _bcmNavigationService: BcmNavigationService,
                private _bcmExportExcelApiService: BcmExportExcelApiService,
    ) {
        super(
            BcmCostCenter,
            costCentersTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService
        );
    }
}
